<script setup lang="ts">
import { useContext } from './context'
import type { Component } from 'vue'
import { type WithAttributes, type Labeled, type Activated, toHtml, fw, type Color } from './attributes'

// #region props
const props = defineProps<WithAttributes<{
  class: {
    color?: Extract<Color, `${string}-solid`> | 'color-primary'
  }
  /**
   * Use when the token is part of a fixed tuple of tokens and removing it entirely would confuse the user
   */
  disabled?: boolean

  /**
    * Pass `RouterLink` or use the `to()` helper
    * @default `<span>` tag
    */
  is?: Component | 'a' | 'button'

  onClick?: (e: MouseEvent) => void
} & Labeled & Activated>>()
// #endregion props

useContext({ uiBlockSize: '24', uiOvershootNone: true })
</script>

<template>
  <component
    :is="is ?? (onClick ? 'button' : 'span')"
    :rel="is === 'a' ? 'noopener noreferrer' : undefined"
    :target="is === 'a' ? '_blank' : undefined"
    v-bind="{ ...$attrs, ...toHtml<'button' | 'a'>(props) }"
    :class="[
      fw.layout.row,
      fw.font.caption,
      !props.class?.includes('shape-') && fw.shape.pill,
      !props.class?.includes('color-') && fw.color.defaultSolid,
      props.class,
      $style.token,
    ]"
    ui-block-size="24"
    ui-nowrap
    ui-raised
    :on-click
  >
    <div
      v-if="$slots.image"
      :class="$style.image"
    >
      <slot name="image" />
    </div>
    <slot />
    <div
      v-if="$slots.action"
      :class="$style.action"
    >
      <slot name="action" />
    </div>
  </component>
</template>

<style module>
.token {
    /**
    * ====================================================
    * Color transitions
    * ====================================================
    */

    transition: background-color .3s;

    &:hover, &:focus-visible {
        transition: none;
    }

    &:is([aria-pressed=true], [aria-expanded=true], [aria-selected=true], [aria-checked=true]):hover:not(:active){
            transition: background-color .3s;
    }

  /**
  * ====================================================
  * Layout
  * ====================================================
  */

  position: relative;

  gap: 8px;
  padding: 0px 10px 0 10px;

  /* Badge placement */

  --badge-inset: -10px -10px auto auto;

  /**
  * ====================================================
  * Interactivity
  * ====================================================
  */

  &:not(:is([href], [onClick])) {
      /* Remove hit-area from non-interactive token but re-enable any interactive element inside slot */
      pointer-events: none;
      > * {
          pointer-events: auto;
      }
  }

  &[href]:not(:is(:hover, :focus)){
      text-decoration-color: transparent;
  }

  /* Add transparent hit area at least 44px squared to satisfy WCAG2 target size requirement AAA: */
  &::after {
    content: '';
    position: absolute;

    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    width: max(48px, 100%);
    height: max(48px, 100%);
  }

/**
* ====================================================
* Slots
* ====================================================
*/

> .image {
  position: relative;
  margin: 0px -4px 0px -10px;
  border-radius: inherit;
  overflow: hidden;
  height: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  > img {
    object-fit: cover;
    height: 100%;
    width: 100%;
  }
}
> .action {
  position: relative;
  margin: 0px -10px 0px -4px;
  border-radius: inherit;
  flex-shrink: 0;
}
}
/* Lists of Tokens in a row overlap each other */

span:has(.token+.token) {
  display: flex;

  >.token+.token:not(:focus-visible):not(:focus-within) {
      mask: radial-gradient(15px circle at -8px center, #0000 99.5%, #000 100%);
  }
  >.token+.token:not(:focus-visible):not(:focus-within):dir(rtl) {
      mask: radial-gradient(15px circle at calc(100% + 8px) center, #0000 99.5%, #000 100%);
  }
}
[ui-raised][ui-sunken] {
    background-color: var(--color-background-500);
    color: var(--color-foreground-50);
}
</style>
